import ApiService from "./ApiService";

/**
 * Service para gerenciar quizzes
 */
class QuizService {
  /**
   * Lista quizzes por curso
   * @param {number} courseId - ID do curso
   * @returns {Promise} Lista de quizzes
   */
  static async listByCourse(courseId) {
    return ApiService.get(`/quizzes`, { courseId });
  }

  /**
   * Busca um quiz por ID
   * @param {number} id - ID do quiz
   * @returns {Promise} Detalhes do quiz
   */
  static async getById(id) {
    return ApiService.get(`/quizzes/${id}`);
  }

  /**
   * Cria um novo quiz
   * @param {object} data - Dados do quiz { courseId, title, description }
   * @returns {Promise} Quiz criado
   */
  static async create(data) {
    return ApiService.post(`/quizzes`, data);
  }

  /**
   * Atualiza um quiz existente
   * @param {number} id - ID do quiz
   * @param {object} data - Dados para atualizar { title, description }
   * @returns {Promise} Quiz atualizado
   */
  static async update(id, data) {
    return ApiService.put(`/quizzes/${id}`, data);
  }

  /**
   * Exclui um quiz
   * @param {number} id - ID do quiz
   * @returns {Promise} Resultado da exclusão
   */
  static async delete(id) {
    return ApiService.delete(`/quizzes/${id}`);
  }

  /**
   * Duplica um quiz
   * @param {number} id - ID do quiz a duplicar
   * @param {string} newTitle - Novo título
   * @returns {Promise} Quiz duplicado
   */
  static async duplicate(id, newTitle) {
    return ApiService.post(`/quizzes/${id}/duplicate`, { newTitle });
  }

  /**
   * Adiciona uma questão ao quiz
   * @param {number} quizId - ID do quiz
   * @param {object} data - Dados da questão
   * @returns {Promise} Questão criada
   */
  static async addQuestion(quizId, data) {
    return ApiService.post(`/quizzes/${quizId}/questions`, data);
  }

  /**
   * Atualiza uma questão
   * @param {number} questionId - ID da questão
   * @param {object} data - Dados para atualizar
   * @returns {Promise} Questão atualizada
   */
  static async updateQuestion(questionId, data) {
    return ApiService.put(`/quizzes/questions/${questionId}`, data);
  }

  /**
   * Exclui uma questão
   * @param {number} questionId - ID da questão
   * @returns {Promise} Resultado da exclusão
   */
  static async deleteQuestion(questionId) {
    return ApiService.delete(`/quizzes/questions/${questionId}`);
  }

  /**
   * Reordena questões do quiz
   * @param {number} quizId - ID do quiz
   * @param {array} questionIds - Array com IDs na nova ordem
   * @returns {Promise} Resultado da reordenação
   */
  static async reorderQuestions(quizId, questionIds) {
    return ApiService.put(`/quizzes/${quizId}/reorder`, { questionIds });
  }

  /**
   * Faz preview de importação de Excel
   * @param {File} file - Arquivo Excel
   * @param {number} courseId - ID do curso
   * @returns {Promise} Preview dos dados
   */
  static async previewExcelImport(file, courseId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('courseId', courseId);

    return ApiService.vueInstance.axios.post(`/quizzes/import/preview`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  /**
   * Confirma importação de Excel
   * @param {object} data - { courseId, parsedData }
   * @returns {Promise} Quiz criado
   */
  static async confirmExcelImport(data) {
    return ApiService.post(`/quizzes/import/confirm`, data);
  }

  /**
   * Busca estatísticas do quiz
   * @param {number} quizId - ID do quiz
   * @returns {Promise} Estatísticas
   */
  static async getStats(quizId) {
    return ApiService.get(`/quizzes/${quizId}/stats`);
  }

  /**
   * Busca estatísticas das questões
   * @param {number} quizId - ID do quiz
   * @returns {Promise} Estatísticas das questões
   */
  static async getQuestionStats(quizId) {
    return ApiService.get(`/quizzes/${quizId}/questions/stats`);
  }

  /**
   * Busca quiz para uma lesson
   * @param {number} lessonId - ID da lesson
   * @returns {Promise} Dados do quiz
   */
  static async getQuizForLesson(lessonId) {
    return ApiService.get(`/lessons/${lessonId}/quiz`);
  }

  /**
   * Inicia uma nova tentativa de quiz
   * @param {number} quizId - ID do quiz
   * @returns {Promise} Dados da tentativa
   */
  static async startAttempt(quizId) {
    return ApiService.post(`/quizzes/${quizId}/attempts`);
  }

  /**
   * Busca questões de uma tentativa
   * @param {number} attemptId - ID da tentativa
   * @returns {Promise} Lista de questões
   */
  static async getAttemptQuestions(attemptId) {
    return ApiService.get(`/quizzes/attempts/${attemptId}/questions`);
  }

  /**
   * Salva resposta de uma questão
   * @param {number} attemptId - ID da tentativa
   * @param {object} data - { questionId, selectedOptionIds }
   * @returns {Promise} Resultado do salvamento
   */
  static async saveAnswer(attemptId, data) {
    return ApiService.put(`/quizzes/attempts/${attemptId}/answers`, data);
  }

  /**
   * Finaliza uma tentativa de quiz
   * @param {number} attemptId - ID da tentativa
   * @param {number} lessonId - ID da lesson (opcional)
   * @returns {Promise} Resultado do quiz
   */
  static async submitAttempt(attemptId, lessonId) {
    const url = `/quizzes/attempts/${attemptId}/submit${lessonId ? `?lessonId=${lessonId}` : ''}`;
    return ApiService.post(url);
  }

  /**
   * Busca detalhes de uma tentativa
   * @param {number} attemptId - ID da tentativa
   * @returns {Promise} Detalhes da tentativa
   */
  static async getAttemptDetails(attemptId) {
    return ApiService.get(`/quizzes/attempts/${attemptId}`);
  }

  /**
   * Busca tentativas de um estudante
   * @param {number} userId - ID do estudante
   * @param {number} courseId - ID do curso (opcional)
   * @returns {Promise} Lista de tentativas
   */
  static async getStudentAttempts(userId, courseId) {
    const params = courseId ? { courseId } : {};
    return ApiService.get(`/students/${userId}/attempts`, params);
  }

  /**
   * Busca engajamento de quiz por curso
   * @param {number} courseId - ID do curso
   * @returns {Promise} Dados de engajamento
   */
  static async getCourseEngagement(courseId) {
    return ApiService.get(`/courses/${courseId}/quiz-engagement`);
  }
}

export default QuizService;
